import React, { useState, useEffect } from 'react';
import { X, Share2, CheckCircle, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { getMentionableUsers, shareCandidateToFeishu } from '../api';
import { Candidate, User } from '../types';

interface Props {
  candidate: Candidate;
  onClose: () => void;
}

export default function ShareCandidateModal({ candidate, onClose }: Props) {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    getMentionableUsers()
      .then((all: User[]) => {
        setUsers(all.filter(u => u.feishu_open_id && u.status === 'active'));
        setLoading(false);
      })
      .catch(() => {
        toast.error('获取用户列表失败');
        setLoading(false);
      });
  }, []);

  const toggle = (id: string) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSend = async () => {
    if (selectedIds.length === 0) { toast.error('请选择至少一位接收人'); return; }
    setSending(true);
    try {
      const result = await shareCandidateToFeishu(candidate.id, selectedIds);
      toast.success(result.message || '分享成功');
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '分享失败');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-[480px] max-h-[80vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-modal-in">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2.5">
            <Share2 size={18} className="text-primary-600" />
            <h2 className="text-lg font-bold text-gray-900">分享候选人到飞书</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Candidate summary */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg px-4 py-3">
            <p className="text-sm font-semibold text-primary-800">{candidate.name}</p>
            <p className="text-xs text-primary-600 mt-0.5">
              {[candidate.position_name, candidate.latest_title, candidate.latest_company].filter(Boolean).join(' · ')}
            </p>
          </div>

          {/* Recipient selection */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">
              选择接收人（已绑定飞书）
            </label>
            {loading ? (
              <div className="text-center py-4 text-gray-400 text-sm">加载中...</div>
            ) : users.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 flex items-center gap-2">
                <AlertCircle size={16} />
                暂无已绑定飞书的用户，请先在团队管理中绑定飞书账号
              </div>
            ) : (
              <div className="space-y-1.5">
                {users.map(u => (
                  <button
                    key={u.id}
                    onClick={() => toggle(u.id)}
                    className={clsx(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left',
                      selectedIds.includes(u.id)
                        ? 'border-primary-300 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    )}
                  >
                    <div className={clsx(
                      'w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors',
                      selectedIds.includes(u.id) ? 'border-primary-600 bg-primary-600' : 'border-gray-300'
                    )}>
                      {selectedIds.includes(u.id) && <CheckCircle size={12} className="text-white" />}
                    </div>
                    <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 shrink-0">
                      {u.display_name?.charAt(0) || u.username.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-800">{u.display_name || u.username}</span>
                      <span className="ml-2 text-[10px] text-gray-400">
                        {u.role === 'admin' ? '管理员' : u.role === 'hr' ? 'HR' : '面试官'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <p className="text-[11px] text-gray-400">
            接收人将在飞书收到候选人信息卡片，可点击直接进入系统查看详情。
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            取消
          </button>
          <button
            onClick={handleSend}
            disabled={sending || selectedIds.length === 0}
            className="px-5 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {sending ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Share2 size={14} />
            )}
            发送到飞书
          </button>
        </div>
      </div>
    </div>
  );
}
